import type { ReactNode } from 'react';
import { Stack } from 'react-bootstrap';
import { ArrayFields, Card, FormInput, FormattedMessageDiv, formatPhoneNumber } from '@odmonorepo/core-components';
import * as yup from 'yup';
import { hasAllowedCharacters } from 'merchant-api';
import { AppData } from 'merchant-api/types';
import { AddressInputSchema, AddressInput } from './AddressInput';

export const BusinessContactSchema = yup.object().shape({
  businesses: yup.array().of(
    yup.object().shape({
      legalName: yup.string().required('formInput.party.businesses.legalName.error').test(
        'validBusinessName',
        'formInput.party.businesses.legalName.invalidCharacters',
        hasAllowedCharacters
      ),
      doingBusinessAs: yup
      .string()
      .nullable()
      .notRequired()
      .test(
        "max_length",
        "Alternate business name must be less than 128 characters.",
        value => !value || value.length < 128
      )
      .test(
        'validBusinessName',
        'formInput.party.businesses.doingBusinessAs.invalidCharacters',
        hasAllowedCharacters
      ),
      addresses: yup.array().of(AddressInputSchema),
      contacts: yup.array().of(
        yup.object({
          value: yup.string().when('type', (type, schema) =>
            type[0] === 'PHONE_NUMBER'
              ? schema.required().test((val) => val?.replace(/\D/g, '').length == 10)
              : schema
          )
        })
      ),
    })
  ),
});

export const BusinessContact = ({ review = false, children }: { review?: boolean; children?: ReactNode }) => {
  return (
    <Card className={review ? 'border-0 shadow-sm' : undefined} bodyClassName={review ? 'p-5' : undefined}>
      <Stack gap={5}>
        {review ? (
          <Stack gap={1}>
            <FormattedMessageDiv className="h4 mb-0" id="businessReview.contactInfo" />
            <FormattedMessageDiv className="fs-6" id="businessReview.contactInfo.description" />
          </Stack>
        ) : (
          <FormattedMessageDiv className="h4" id={'renewalApp.businessInfo'}/>
        )}
        <FormInput<AppData> name="party.businesses.0.legalName" data-testid="businessLegalName" useYupErrors />
        <FormInput<AppData> 
          name="party.businesses.0.doingBusinessAs" 
          label={
            <FormattedMessageDiv id={`formInput.party.businesses.doingBusinessAs`}
            values={{
              br:(<br className="br-mobile"/>),
              optional: (<span className="text-gray">(Optional)</span>)  
            }}
            />
          }
          data-testid="doingBusinessAsInput"
          useYupErrors
          className="label-multiline"
        />
        <AddressInput<AppData> namePrefix="party.businesses.0" />
        <ArrayFields<AppData> name={`party.businesses.0.contacts`} data-testid="businessContacts">
          {({ fields }) => {
            return (
              <>
                {fields.map((contact, j) => {
                  if (contact.type === 'PHONE_NUMBER') {
                    return (
                      <FormInput<AppData> key={j} name={`party.businesses.0.contacts.${j}.value`} data-testid="businessPhoneNumber" stripFormatOnSave formatValue={formatPhoneNumber} label={<FormattedMessageDiv id='formInput.business.phone'/>} controlProps={{ inputMode: 'tel' }}/>
                    )}
                })}
              </>);
          }}
        </ArrayFields>
        {children}
      </Stack>
    </Card>
  );
};

export default BusinessContact;
